import { useDispatch } from "react-redux";
import { setLayoutDashboardTitle } from "../../../store/ui-slice";
import { useContext, useEffect, useRef, useState } from "react";
import useAPIRequest from "../../../custom_hooks/simple/useAPIRequest";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { AutoComplete } from "primereact/autocomplete";
import { useNavigate } from "react-router-dom";
import { format, set } from "date-fns";
import UiContext from "../../../store/ui-context";
import { ProgressSpinner } from "primereact/progressspinner";
import { useReactToPrint } from "react-to-print";
import HeaderTemplate from "./report-template-header";
import DetailTemplate from "./report-template-detail";
import TableHeaderTemplate from "./report-template-table-header";
import GroupHeaderTemplate from "./report-template-group-header";
import GroupFooterTemplate from "./report-template-group-footer";
import SummaryTemplate from "./report-template-summary";
import reformatListData from "./report-template-reformat-data";
import reformatListCopyData from "./report-template-reformat-copy-data";
import {
  columnSettings,
  groupSetting,
  tableWidth,
  title,
  generateDefaultReportParams,
  paramSettings,
} from "./report-template-setting";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import KendaraanSelectorDialog from "../../../dialogs/selectors/KendaraanSelectorDialog";
import useIsMobile from "../../../hooks/useIsMobile";
import MobileList from "./laporan-mobile-list";

const LaporanMasalahMobilPerUnitPage = () => {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({
    contentRef,
  });
  const uiContext = useContext(UiContext);
  const dispatch = useDispatch();

  // const [summaryOnNewPage, setSummaryOnNewPage] = useState(false);

  const { requestGet, requestGetOne, requestPost, requestDelete, loading } =
    useAPIRequest();
  const [reportParam, setReportParam] = useState(generateDefaultReportParams());
  const [reportControl, setReportControl] = useState({ zoom: 100 });
  const [paging, setPaging] = useState({
    page: 1,
    take: 1000000,
    filter: "",
    total: 0,
  });
  const [listData, setListData] = useState([]);
  const [listRawData, setListRawData] = useState([]);
  const [groupMap, setGroupMap] = useState({});
  const [groupNames, setGroupNames] = useState([]);
  const isMobile = useIsMobile();

  const [visibleKendaraanSelector, setVisibleKendaraanSelector] =
    useState(false);

  // use effect section

  useEffect(() => {
    dispatch(setLayoutDashboardTitle({ title }));
  }, []);

  // API call section

  const fetchListData = async ({ page, take, filter }) => {
    if (!reportParam.data_kendaraan) {
      uiContext.showErrorMsg("Error", "Pilih kendaraan terlebih dahulu");
      return;
    }

    if (loading) return;
    const _filter = filter ? filter : getFilter();

    return requestGet({
      fullUrl: `/api/problem/problemkendaraan/data`,
      params: {
        page: page,
        take: take,
        filter: _filter,
        order: "kendaraan_id",
        order_method: "ASC",
        with_kendaraan: 1,
        with_pengecekan_mobil_detil: 1,
        "with_pengecekan_mobil_detil,data_pengecekan_mobil,data_verif_keamanan": 1,
        "with_pengecekan_mobil_detil,data_pengecekan_mobil,data_verif_staff": 1,
        "with_pengecekan_mobil_detil,data_pengecekan_mobil,data_verif_supir": 1,
      },
      onSuccess: ({ pagination, message, data }) => {
        setPaging({
          ...paging,
          total: pagination["total"],
          page: pagination["page"],
          take: pagination["take"],
          filter: _filter,
        });

        const newData = data;

        // // testing big number of data - START
        // const newData = [];
        // let newId = 1;
        // for (let i = 0; i < 1000; i++) {
        //   for (let j = 0; j < data.length; j++) {
        //     const el = { ...data[j], id: newId++ };
        //     newData.push(el);
        //   }
        // }
        // // console.log("BIG DATA RESULT ", newData);
        // console.log("BIG DATA RESULT ", newData.length);
        // // testing big number of data - END

        // reformat fetched data
        const {
          list: _listData,
          groupMap,
          groupNames,
        } = reformatListData(newData, paging.page, paging.take);
        setListData(_listData, page, take);
        setListRawData(newData);
        setGroupMap(groupMap);
        setGroupNames(groupNames);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  // other function section

  const getFilter = () => {
    let filterArray = [];
    for (let setting of paramSettings) {
      let singleFilter = undefined;
      const value = reportParam[setting.name];
      const nullable = setting.nullable ?? false;
      switch (setting.type) {
        case "date":
          if (value || nullable) {
            singleFilter = `${setting.field}:${setting.operator}:${value ? format(value, setting.format) : "null"
              }`;
          }
          break;
        default:
          if (value || nullable) {
            singleFilter = `${setting.field}:${setting.operator}:${value}`;
          }
          break;
      }
      filterArray.push(singleFilter);
    }

    if (reportParam.data_kendaraan) {
      filterArray.push(`kendaraan_id:=:${reportParam.data_kendaraan.id}`);
    }
    filterArray.push("tanggal_selesai:=:Null");
    filterArray.push("data_kendaraan.kendaraan_jenis_id:=:2");

    return filterArray.filter((el) => el).join(";");
  };

  const getCopyClipboardData = (listRaw) => {
    let result = "";

    if (listRaw && listRaw.length > 0) {
      const firstRow = listRaw[0];
      const listOfKeys = Object.keys(firstRow).filter(
        (key) =>
          !key.startsWith("data_") &&
          typeof firstRow[key] !== "object" &&
          !key.includes("id") &&
          !key.includes("_id")
      );

      for (let key of listOfKeys) {
        result += `${key}\t`;
      }
      result += "\n";

      for (let row of listRaw) {
        for (let key of listOfKeys) {
          const data = row[key];
          result += `${data}\t`;
        }
        result += "\n";
      }
    }

    return result;
  };

  // UI handler section

  const handleGenerateReport = () => {
    fetchListData({ page: paging.page, take: paging.take });
  };

  const handleCopyToClipboard = () => {
    const copyText = getCopyClipboardData(reformatListCopyData(listRawData));
    navigator.clipboard
      .writeText(copyText)
      .then(() => {
        uiContext.showSuccessMsg("Success", "Data berhasil di-copy");
      })
      .catch(() => {
        uiContext.showErrorMsg("Error", "Gagal meng-copy data");
      });
  };

  const hanldeOnConfirmKendaraanSelector = (event, selected) => {
    if (selected && selected.length >= 1) {
      const firstSelected = selected[0];
      setReportParam({
        ...reportParam,
        data_kendaraan: firstSelected,
      });

      setVisibleKendaraanSelector(false);
    }
  };

  const handleOnClickBack = () => {
    const accept = async () => {
      if (isMobile) {
        navigate("/laporan");
      } else {
        window.close();
      }
    };
    const reject = () => { };

    confirmDialog({
      message: `Menutup bon ini, lanjutkan?`,
      header: "Konfirmasi ",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      acceptLabel: "Konfirmasi",
      rejectLabel: "Tidak",
      acceptClassName: "p-button-danger",
      accept,
      reject,
    });
  };

  // custom UI section

  return (
    <div className={`col-start-start p-4 gap-4 w-100`}>
      <KendaraanSelectorDialog
        visible={visibleKendaraanSelector}
        onHide={() => setVisibleKendaraanSelector(false)}
        onConfirm={hanldeOnConfirmKendaraanSelector}
        additionalFilter={`kendaraan_jenis_id:=:2`}
      />
      <ConfirmDialog />
      {/* report header section */}
      <div className={`elevated-card-tight-vertical ${isMobile ? "col-between-start" : "row-between-end"} gap-2 !px-4 w-100`}>
        <div className="row-start-center gap-4">
          {/* filter - from */}
          <div className="col-start-start gap-1">
            <span className="text-sm font-semibold text-slate-500 tracking-wider ">
              Dari:
            </span>
            <Calendar
              showIcon
              className="max-w-[11rem]"
              value={reportParam.dari}
              onChange={(e) =>
                setReportParam({ ...reportParam, dari: e.value })
              }
              locale="id"
            />
          </div>
          {/* filter - sampai */}
          <div className="col-start-start gap-1">
            <span className="text-sm font-semibold text-slate-500 tracking-wider ">
              Sampai:
            </span>
            <Calendar
              showIcon
              className="max-w-[11rem]"
              value={reportParam.sampai}
              onChange={(e) =>
                setReportParam({ ...reportParam, sampai: e.value })
              }
              locale="id"
            />
          </div>
          {/* filter - kendaraan */}
          {isMobile ? null : (
            <div className="col-start-start gap-1">
              <span className="text-sm font-semibold text-slate-500 tracking-wider ">
                Plat Kendaraan:
              </span>
              <Button
                className={`row-start-center min-w-[12rem] grow bg-indigo-50 gap-2`}
                outlined
                onClick={(e) => {
                  setVisibleKendaraanSelector(true);
                }}
              >
                <div className="row-between-center w-100 gap-2">
                  {!reportParam.data_kendaraan
                    ? "-"
                    : reportParam.data_kendaraan.nomor_polisi}
                  <i className="pi pi-search" />
                </div>
              </Button>
            </div>
          )}
        </div>
        <div className="row-end-end gap-3">
          {isMobile ? (
            <div className="col-start-start gap-1">
              <span className="text-sm font-semibold text-slate-500 tracking-wider ">
                Plat Kendaraan:
              </span>
              <Button
                className={`row-start-center min-w-[12rem] grow bg-indigo-50 gap-2`}
                outlined
                onClick={(e) => {
                  setVisibleKendaraanSelector(true);
                }}
              >
                <div className="row-between-center w-100 gap-2">
                  {!reportParam.data_kendaraan
                    ? "-"
                    : reportParam.data_kendaraan.nomor_polisi}
                  <i className="pi pi-search" />
                </div>
              </Button>
            </div>
          ) : null}
          <Button
            icon="pi pi-refresh"
            label="Generate"
            onClick={() => handleGenerateReport()}
          />
        </div>
      </div>
      {/* report control section */}
      <div className="elevated-card-tight-vertical row-between-center w-full">
        <div className="row-center-center gap-4">
          <Button
            className="max-h-[2rem] min-w-[2rem]"
            icon="pi pi-arrow-left"
            severity="danger"
            onClick={handleOnClickBack}
          />

          {/* <div className="row-center-center gap-2">
            <Checkbox
              checked={summaryOnNewPage}
              onChange={(e) => setSummaryOnNewPage(e.checked)}
            />
            <span className="text-sm text-slate-600">Summary on new page</span>
          </div> */}
        </div>
        {!isMobile && (
          <>
            <Divider layout="vertical" className="max-h-[0.9rem]" />
            <div className="row-center-center gap-3">
              {/* <CopyToClipboard
            text={getCopyClipboardData(listRawData)}
            onCopy={handleCopyToClipboardSuccess}
          > */}
              <Button
                icon="pi pi-copy"
                className="p-button-outlined"
                label="COPY"
                onClick={() => handleCopyToClipboard()}
              />
              {/* </CopyToClipboard> */}
              <Button
                icon="pi pi-print"
                className="p-button-outlined"
                label="PRINT"
                onClick={() => reactToPrintFn()}
              />
            </div>
          </>
        )}
      </div>

      {/* report content section  */}

      <div className="col-center-center w-full">
        {isMobile ? (
          <MobileList listData={listData} loading={loading} />
        ) : (
          <>
            {loading && (
              <div className="col-start-start bg-white shadow-md p-3 border border-zinc-300 w-[55rem]">
                <ProgressSpinner />
              </div>
            )}
            {!loading && (
              <div
                ref={contentRef}
                className={`printContent col-start-start bg-white shadow-md print:shadow-none p-3 border print:border-0 border-zinc-300 `}
              >
                <HeaderTemplate reportParam={reportParam} />
                <table className={`${tableWidth}`}>
                  <thead>
                    <TableHeaderTemplate />
                  </thead>
                  {/* <div id="report-virtual-paging-header"></div> */}
                  <tbody>
                    {listData.map((el) => {
                      if (el.report_type === "group-header")
                        return <GroupHeaderTemplate key={el.mykey} {...el} />;
                      else if (el.report_type === "group-footer")
                        return <GroupFooterTemplate key={el.mykey} {...el} />;
                      else return <DetailTemplate key={el.mykey} {...el} />;
                    })}
                  </tbody>
                  {/* <div id="report-virtual-paging-footer"></div> */}
                </table>
                <div className="h-[2rem]" />
                {/* {summaryOnNewPage && <div className="page-break" />} */}
                {/* <SummaryTemplate
              listRaw={listRawData}
              list={listData}
              groupNames={groupNames}
              groupMap={groupMap}
            /> */}
                <div className="h-[2rem]" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default LaporanMasalahMobilPerUnitPage;
